import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

type Params<TOption, TVariables, TResult> = {
  value: TOption | null;
  options?: TOption[] | null;
  pendingOption?: TOption | null;
  isSame: (option: TOption, selected: TOption) => boolean;
  sync?: {
    mutationFn: (variables: TVariables) => Promise<TResult>;
    invalidateQueryKey?: QueryKey;
    /**
     * Receives what the sync endpoint returned, so the caller can put the server's state back into
     * the form. Invalidating a query is not enough on its own: a react-hook-form field is
     * controlled, so a refetch never reaches it, and a sync can legitimately clear a selection (a
     * contact person that left the institution) rather than merely refresh it.
     */
    onSynced?: (result: TResult) => void;
    successMessage: string;
    errorMessage: string;
  };
};

export function useSyncableSelection<TOption, TVariables = never, TResult = void>({
  value,
  options,
  pendingOption,
  isSame,
  sync,
}: Params<TOption, TVariables, TResult>) {
  const queryClient = useQueryClient();

  const availableOptions = useMemo(() => {
    const baseOptions = pendingOption ? [...(options ?? []), pendingOption] : (options ?? []);

    if (value && !baseOptions.some((option) => isSame(option, value))) {
      return [...baseOptions, value];
    }

    return baseOptions;
  }, [options, pendingOption, value, isSame]);

  const selectedValue = useMemo(() => {
    if (!value) return null;

    return availableOptions.find((option) => isSame(option, value)) ?? value;
  }, [availableOptions, value, isSame]);

  const syncMutation = useMutation({
    mutationFn: async (variables: TVariables) => {
      if (!sync) return undefined;
      return await sync.mutationFn(variables);
    },
    onSuccess: async (result) => {
      if (!sync) return;

      // Before the invalidation: the refetch is what repopulates the option list, and the caller
      // may drop the current selection here. Applying the authoritative value first keeps the
      // Autocomplete from briefly re-resolving a selection the sync has just removed.
      if (result !== undefined) {
        sync.onSynced?.(result);
      }

      if (sync.invalidateQueryKey) {
        await queryClient.invalidateQueries({
          queryKey: sync.invalidateQueryKey,
        });
      }

      toast.success(sync.successMessage);
    },
    onError: () => {
      if (!sync) return;
      toast.error(sync.errorMessage);
    },
  });

  return {
    availableOptions,
    selectedValue,
    triggerSync: syncMutation.mutate,
    isSyncing: syncMutation.isPending,
  };
}
